/**
 * "What you'll do today" — the tech's view of the block. The SAME component
 * renders on the iPad and inside the office's "See what the tech sees", so
 * the preview can't drift from what the tech actually reads.
 *
 * Drawn in fixed light colours with an opaque surface: the field app's
 * blueprint watermark sits behind everything (AGENTS.md frontend rule), and
 * the office preview is a picture of the iPad, so it doesn't follow the theme.
 */
import { useState } from "react";
import { cn } from "@/lib/utils";
import { InfoButton } from "@/components/info-button";
import { ACTION_CHIP, useTechPlan, type TechPlanLine, type TechPlanTechView } from "@/lib/tech-plan";
import { TECH_PLAN_HELP } from "./tech-plan-help";

export function ActionChip({
    action,
    number,
    count,
}: {
    action: TechPlanLine["action"];
    number?: number;
    count?: number;
}) {
    if (!action) return null;
    const chip = ACTION_CHIP[action];
    return (
        <span
            className={cn(
                "inline-flex w-fit shrink-0 items-center self-start justify-self-start rounded px-1.5 py-1 text-[11px] font-extrabold uppercase leading-none tracking-wide",
                chip.className,
            )}
            data-testid={`action-chip-${action}`}
        >
            {chip.label}
            {count && count > 1 && number ? ` · ${number}` : ""}
        </span>
    );
}

export function TechPlanBlock({
    plan,
    preview = false,
    readAt = null,
}: {
    plan: TechPlanTechView;
    /** Inside the office's preview: no ⓘ (it would open on the office's screen anyway). */
    preview?: boolean;
    /** When the tech last tapped Got it on "Read your plan" (#2017) and it still
     *  holds; a line the office changed after that is marked. The field page passes it. */
    readAt?: string | null;
}) {
    const [woOpen, setWoOpen] = useState(false);
    const deviceCount = plan.lines.filter((l) => l.assetId).length;
    // threads/job-page-fresh (migration 721): a line the office changed after
    // dispatch stays yellow, old words crossed out, until the tech taps Got it
    // on "Read your plan" (#2017, job_tech_plan_reads) after that change. The
    // office's preview shows every change.
    const isNew = (l: TechPlanLine) =>
        !!l.changedAt && (preview || !readAt || new Date(l.changedAt).getTime() > new Date(readAt).getTime());
    const jobItems = [
        ...plan.autoLines.map((a) => ({ icon: a.icon, text: a.text, from: a.from })),
        ...plan.jobWide.map((j) => ({ icon: j.icon, text: j.text, from: null as string | null })),
    ];
    return (
        <section
            aria-label="What you'll do today"
            data-testid="tech-plan-block"
            className="space-y-2.5 rounded-xl border-2 border-[#7a5c0a] bg-white p-4 text-stone-900 shadow-sm"
        >
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <h2 className="text-lg font-extrabold leading-tight tracking-tight">What you'll do today</h2>
                    {!preview && <InfoButton topic={TECH_PLAN_HELP.techToday} />}
                </div>
                {plan.changedByOffice && (
                    <span
                        className="rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-900"
                        data-testid="tech-plan-changed"
                    >
                        Changed by the office · {plan.changedByOffice.atWords}
                    </span>
                )}
            </div>

            {plan.lines.length === 0 ? (
                <p className="text-sm text-stone-600">
                    The office hasn't written anything for this job yet. Call them if you're not sure.
                </p>
            ) : (
                <div className="divide-y divide-stone-200">
                    {plan.lines.map((l) => (
                        <div
                            key={l.key}
                            className={
                                isNew(l)
                                    ? "-mx-2 space-y-1 rounded-lg bg-yellow-100 px-2 py-2.5"
                                    : "space-y-1 py-2.5 first:pt-0 last:pb-0"
                            }
                            data-testid="tech-plan-line"
                            data-changed={isNew(l) ? "yes" : undefined}
                        >
                            {isNew(l) && (
                                <span className="block text-[11px] font-extrabold uppercase tracking-wide text-yellow-900">
                                    Changed by the office · {lineChangeWords(l.changedAt!)}
                                </span>
                            )}
                            {isNew(l) && l.was && (
                                <span
                                    className="block text-[14px] text-stone-500 line-through"
                                    data-testid="tech-plan-was"
                                >
                                    {l.was}
                                </span>
                            )}
                            <ActionChip action={l.action} number={l.number} count={deviceCount} />
                            <p className="text-[17px] font-semibold leading-snug">{l.text}</p>
                            <LineFacts line={l} audience="tech" />
                        </div>
                    ))}
                </div>
            )}

            {jobItems.length > 0 && (
                <div className="space-y-1 border-t border-stone-200 pt-2.5" data-testid="tech-plan-job-wide">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-stone-500">
                        For the whole job
                    </div>
                    <ul className="space-y-1">
                        {jobItems.map((j, i) => (
                            <li key={i} className="grid grid-cols-[22px_minmax(0,1fr)] gap-1 text-[15px] leading-snug">
                                <span aria-hidden className="text-center">
                                    {j.icon}
                                </span>
                                <span>
                                    {j.text}
                                    {j.from && <span className="ml-1.5 text-xs text-stone-400">from {j.from}</span>}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-dashed border-stone-200 pt-2 text-xs text-stone-500">
                <span>From the office · the same words they see</span>
                {plan.workOrderText && (
                    <button
                        type="button"
                        className="min-h-[44px] font-semibold text-[#7a5c0a]"
                        onClick={() => setWoOpen((o) => !o)}
                        aria-expanded={woOpen}
                        data-testid="read-work-order"
                    >
                        {woOpen ? "Hide the work order ▴" : "Read the work order ›"}
                    </button>
                )}
            </div>
            {woOpen && plan.workOrderText && (
                <div
                    className="max-h-80 overflow-y-auto whitespace-pre-line rounded-lg border border-stone-200 bg-stone-50 p-3 text-sm leading-snug"
                    data-testid="work-order-text"
                >
                    {plan.workOrderText}
                </div>
            )}
        </section>
    );
}

/** "2:10 PM" today, else "Oct 27, 2:10 PM" (Central). */
function lineChangeWords(iso: string): string {
    const d = new Date(iso);
    const opts = { timeZone: "America/Chicago" } as const;
    const today = new Date().toLocaleDateString("en-US", opts) === d.toLocaleDateString("en-US", opts);
    const time = d.toLocaleTimeString("en-US", { ...opts, hour: "numeric", minute: "2-digit" });
    return today ? time : `${d.toLocaleDateString("en-US", { ...opts, month: "short", day: "numeric" })}, ${time}`;
}

/** The gray facts under a line: where it is, why, what's on the truck. */
export function LineFacts({ line, audience }: { line: TechPlanLine; audience: "tech" | "office" }) {
    const work = line.action === "repair" || line.action === "replace";
    const facts: Array<{ k: string; v: React.ReactNode }> = [];
    if (line.where) facts.push({ k: "Where", v: line.where });
    if (line.why) facts.push({ k: "Why", v: line.why });
    if (work) {
        facts.push({
            k: "On the truck",
            v:
                line.parts.length > 0 ? (
                    line.parts.join(" · ")
                ) : audience === "office" ? (
                    <span className="font-semibold text-amber-800">no parts ordered yet</span>
                ) : (
                    "nothing ordered, ask the office"
                ),
        });
    }
    if (facts.length === 0) return null;
    return (
        <div className="space-y-0.5 text-[13px] leading-snug text-stone-600">
            {facts.map((f) => (
                <div key={f.k}>
                    <span className="font-semibold text-stone-500">{f.k}:</span> {f.v}
                </div>
            ))}
        </div>
    );
}

/**
 * The device's own line, again, at the top of its repair or replace step —
 * right where the tech types the repair notes.
 */
export function DeviceTechLine({ line }: { line: TechPlanLine | null | undefined }) {
    if (!line) return null;
    return (
        <div
            className="space-y-1 rounded-lg border-2 border-[#7a5c0a] bg-white p-3 text-stone-900"
            data-testid="device-tech-line"
        >
            <div className="flex items-center gap-2">
                <ActionChip action={line.action} />
                <span className="text-[11px] font-bold uppercase tracking-wider text-stone-500">From the office</span>
            </div>
            <p className="text-base font-semibold leading-snug">{line.text}</p>
            <LineFacts line={line} audience="tech" />
        </div>
    );
}

/** The device's line, fetched for a step that only knows the job and the device. */
export function DeviceTechLineFor({
    jobId,
    assetId,
}: {
    jobId: string | null | undefined;
    assetId: string | null | undefined;
}) {
    const q = useTechPlan(jobId, { asTech: true });
    const plan = q.data?.audience === "tech" ? q.data.plan : null;
    const line = plan?.lines.find((l) => !!assetId && (l.assetIds ?? (l.assetId ? [l.assetId] : [])).includes(assetId));
    return <DeviceTechLine line={line ?? null} />;
}
